import {Component, DoCheck, OnChanges, OnInit, SimpleChanges, ViewEncapsulation} from '@angular/core';
import { MenuTypes } from '../../services/flowBuilder/utils/flow-step-types.enum';
import {FlowMetaData, SharedService} from '../../../../core/services/store/shared-store/shared-storage.service';
import { RolesService } from '../../../../core/services/roles/roles.service';
import {
  selectActionNodeTypes,
  selectChatHubNodesTypes, selectFlowMetaData, selectIntegrationsNodeTypes,
  selectMenuNodeTypes
} from "../../../../core/state/selectors/flow.selector";
import { map } from "rxjs/internal/operators";
import { IntegrationsNodes, NodeObject } from "../../../../shared/utils/nodeTypes";
import {concat, merge, Observable, pipe} from "rxjs";
import { Store } from "@ngrx/store";
import { tap } from "rxjs/operators";

import sortBy from 'lodash/sortBy';
import reverse from 'lodash/reverse';
import remove from 'lodash/remove';
import {loadChatHubProviders} from "../../../../core/state/actions/flow.action";
import {StateService} from "../../../../core/services/store/state/state.service";
import {FlowType} from "../../../../shared/models/state/Flow";


@Component({
  selector: 'app-new-node',
  templateUrl: './new-node.component.html',
  styleUrls: ['./new-node.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class NewNodeComponent implements OnInit, DoCheck {
  isUserInternal: boolean;
  isUserExternal: boolean;
  searchValue: string = '';
  active: string = '';
  sortNodesBy: string = 'type';
  menuNodesTypes = [
    { key: 'Display', title: 'Display Nodes' },
    { key: 'Products', title: 'Product Nodes' },
    { key: 'Input', title: 'Input Nodes' },
    { key: 'Commerce', title: 'Commerce' },
    { key: 'Logic', title: 'Branch / Logic Split' },
  ];

  sortedByTitles = {
    type: 'Node Type',
    most_used: 'Most Used',
    alphabetical: 'Alphabetical',
  };

  useNodeCountsList = {
    // Menus
    [MenuTypes.ASKINPUT]: 0,
    [MenuTypes.DISPLAYMENU]: 0,
    [MenuTypes.MENUOPTION]: 0,
    [MenuTypes.MULTIPLECHOICE]: 0,
    [MenuTypes.EXTERNALLINK]: 0,
    [MenuTypes.CONTACTSENDING]: 0,
    [MenuTypes.PAYMENT]: 0,
    [MenuTypes.AGENTHANDOFF]: 0,
    [MenuTypes.CHATDESK]: 0,
    [MenuTypes.MEDIA]: 0,
    [MenuTypes.RICHLINK]: 0,
    [MenuTypes.TIMEPICKER]: 0,
    [MenuTypes.OTPMENU]: 0,
    [MenuTypes.ORCA_PAYMENT]: 0,
    [MenuTypes.OAUTH]: 0,
    [MenuTypes.IMESSAGE]: 0,
    [MenuTypes.CHAT2PAY]: 0,
    [MenuTypes.MULTI_PRODUCT]: 0,
    [MenuTypes.MENUFORM]: 0,
    [MenuTypes.CONTACTRECEIVING]: 0,
    // Actions
    dateFormat: 0,
    sendTemplate: 0,
    displayList: 0,
    encrypt: 0,
    httpCall: 0,
    isEmpty: 0,
    isNull: 0,
    math: 0,
    decimalFormat: 0,
    regularExpression: 0,
    remap: 0,
    redis: 0,
    chatHub: 0,
    // Integrations
    api: 0,
    nested: 0,
    nestedNode: 0,
  };

  menuNodeTypes: Observable<NodeObject[]>;
  actionNodeTypes: Observable<NodeObject[]>;
  chatHubNodeTypes: Observable<NodeObject[]>;
  integrationsNodeTypes: Observable<NodeObject[]>;
  public currentFlowType: FlowType;

  constructor(public sharedService: SharedService, public rolesService: RolesService, private store: Store,
              private stateService: StateService) { }

  ngOnInit() {
    this.isUserInternal = this.rolesService.isUserInternal();

    if (!localStorage.getItem('nodes')) {
      localStorage.setItem('nodes', JSON.stringify(this.useNodeCountsList));
    }

    this.menuNodeTypes = this.store.select(selectMenuNodeTypes).pipe(
        this.customPipe(),
        map(node => {
          if (this.sharedService.getEntityType().toUpperCase() === 'CLIENT') return node;
          return [];
        }),
        map((nodes: NodeObject[]) => {
          remove(nodes, ({nodeType}) => (nodeType === 'payment'));
          return nodes;
        })
    );
    this.actionNodeTypes = this.store.select(selectActionNodeTypes).pipe(this.customPipe());
    this.chatHubNodeTypes = this.store.select(selectChatHubNodesTypes).pipe(this.customPipe());
    this.integrationsNodeTypes = this.store.select(selectIntegrationsNodeTypes).pipe(
      this.integrationPipe(this.sharedService.getEntityType()),
      this.customPipe()
    );

    this.currentFlowType = this.stateService.getFlowMetaData().flowType;
  }

  ngDoCheck(): void {
    this.currentFlowType = this.stateService.getFlowMetaData()?.flowType;
  }

  showAssets(e: Event, nodes: string) {
    e.preventDefault();
    e.stopPropagation();
    this.active = nodes;
  }

  clearSearch() {
    this.searchValue = '';
  }

  customPipe = () => {
    return pipe(
      this.mostUsedPipe()
    )
  }

  mostUsedPipe = () => {
    return pipe(
      map((nodes: NodeObject[]) => {
        if (this.sortNodesBy !== 'most_used') {
          return nodes
        }
        const nodesCount = JSON.parse(localStorage.getItem('nodes'));
        const withCount = []
        for (const n of nodes) {
          const count = nodesCount[n.nodeType] ? nodesCount[n.nodeType] : 0
          withCount.push({ ...n, count })
        }
        return withCount
      }),
      map(nodes => this.sortNodes(nodes, this.sortNodesBy)),
    )
  }

  integrationPipe = (entityType) => {
    return pipe(
      map((nodes: IntegrationsNodes) => {
        if (!this.isUserInternal) {
          const excludedNodeTypes = ['nestedNode', 'nested', 'async_api'];
          return nodes['default'].filter((node: NodeObject) => !excludedNodeTypes.includes(node.nodeType));
        }
        if (entityType === "VENDOR" || entityType === "FUNDING_SOURCE") {
          const nodeToReturn = nodes[this.sharedService.getEntityType()].filter((node: NodeObject) => node.nodeType === this.sharedService.getFlowName)
          if (nodeToReturn.length) {
            return nodeToReturn.concat(nodes.default)
          }
        }
        return nodes.default;
      })
    )
  }

  sortByChange(sortBy: string) {
    const active = this.active;
    this.active = "";
    this.sortNodesBy = sortBy;
    setTimeout( () => {
      this.active = active;
    });
  }

  sortNodes(nodes, sortType: string) {
    switch (sortType) {
      case 'alphabetical':
        return sortBy(nodes, ['title']);
      case 'most_used':
        const sorted = reverse(sortBy(nodes, ['title']));
        return reverse(sortBy(sorted, ['count']));
      case 'type':
        return sortBy(nodes, ['nodeType']);
      default:
        return nodes;
    }
  }

  reloadChatHubProviders(): void {
    this.store.dispatch(loadChatHubProviders());
  }

  protected readonly FlowType = FlowType;
}
